// Single source of design tokens. Components reference these (or the MUI theme
// built from them in hooks/useAppTheme.js) instead of hard-coded colors.
export const uiPalette = {
  light: {
    background: "#f0f2f5",
    paper: "#ffffff",
    subtle: "#f7f8f9",
    text: "#111827",
    textSecondary: "rgba(17,24,39,0.62)",
    border: "rgba(17,24,39,0.08)",
    borderStrong: "rgba(17,24,39,0.16)",
    primary: "#6b7280",
    primaryHover: "#4b5563",
    primaryDisabled: "#d1d5db",
    fieldBg: "rgba(255,255,255,0.86)",
    avatar: "#9ca3af",
    danger: "#dc2626",
    dangerHover: "#b91c1c",
    dangerDisabled: "#f3b4b4",
    dangerSoft: "rgba(220,38,38,0.08)",
    dangerBorder: "rgba(220,38,38,0.2)",
    shadowPanel: "0 10px 28px rgba(17,24,39,0.06)",
    shadowShell: "0 18px 40px rgba(17,24,39,0.08)",
    shadowPopover: "0 12px 32px rgba(17,24,39,0.12)",
    shadowHover: "0 8px 22px rgba(17,24,39,0.08)",
    shadowSoft: "0 2px 7px rgba(17,24,39,0.06)",
    backdrop: "rgba(17,24,39,0.28)",
  },
  dark: {
    background: "#191816",
    paper: "#211f1d",
    subtle: "#2a2825",
    text: "#f9fafb",
    textSecondary: "rgba(249,250,251,0.62)",
    border: "rgba(255,255,255,0.08)",
    borderStrong: "rgba(255,255,255,0.18)",
    primary: "#7a746b",
    primaryHover: "#625d56",
    primaryDisabled: "rgba(122,116,107,0.42)",
    fieldBg: "rgba(31,31,31,0.96)",
    avatar: "#9a948b",
    danger: "#dc2626",
    dangerHover: "#b91c1c",
    dangerDisabled: "rgba(220,38,38,0.38)",
    dangerSoft: "rgba(248,113,113,0.12)",
    dangerBorder: "rgba(248,113,113,0.28)",
    shadowPanel: "0 10px 28px rgba(0,0,0,0.28)",
    shadowShell: "0 20px 44px rgba(0,0,0,0.34)",
    shadowPopover: "0 14px 36px rgba(0,0,0,0.45)",
    shadowHover: "0 8px 22px rgba(0,0,0,0.35)",
    shadowSoft: "0 2px 7px rgba(0,0,0,0.25)",
    backdrop: "rgba(0,0,0,0.5)",
  },
};

export const statusColors = {
  assigned: "#6b7280",
  active: "#7c3aed",
  completed: "#22c55e",
  completedLate: "#ea580c",
  overdue: "#ef4444",
  total: "#6b7280",
};

// Kept for components rendered outside the app theme (login screen).
export const uiColors = {
  primary: uiPalette.light.primary,
  primaryHover: uiPalette.light.primaryHover,
  danger: uiPalette.light.danger,
  dangerHover: uiPalette.light.dangerHover,
  border: uiPalette.light.border,
  borderStrong: uiPalette.light.borderStrong,
  fieldBgLight: uiPalette.light.fieldBg,
  fieldBgDark: uiPalette.dark.fieldBg,
};

export const uiRadii = {
  shell: 5,
  panel: 4,
  card: 3,
  item: 2,
  field: 2.25,
  pill: 999,
};

export const uiMotion = {
  ease: [0.22, 1, 0.36, 1],
  fast: 0.18,
  base: 0.28,
  slow: 0.42,
};

// Shared framer-motion presets.
export const fadeUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: uiMotion.base, ease: uiMotion.ease },
};

export const popIn = {
  initial: { opacity: 0, scale: 0.96, y: 6 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.97, y: 4 },
  transition: { duration: uiMotion.fast, ease: uiMotion.ease },
};

export const collapse = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: { duration: uiMotion.base, ease: uiMotion.ease },
  style: { overflow: "hidden" },
};

export const staggerDelay = (index, step = 0.035, max = 10) =>
  Math.min(index, max) * step;

export const shellCardSx = {
  borderRadius: uiRadii.shell,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: (theme) => theme.palette.ui.shadowShell,
  backgroundImage: "none",
};

export const panelSx = {
  borderRadius: uiRadii.panel,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: (theme) => theme.palette.ui.shadowPanel,
  p: 2.5,
};

// Inner block of a panel (settings group, list row).
export const blockSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: uiRadii.card,
  bgcolor: "background.default",
};

export const dialogSx = {};

export const fieldSx = (isDark = false) => {
  const p = isDark ? uiPalette.dark : uiPalette.light;
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: uiRadii.field,
      backgroundColor: p.fieldBg,
      color: p.text,
      transition: "box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(17,24,39,0.1)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(17,24,39,0.16)" },
      "&.Mui-focused": { boxShadow: "none" },
      "&.Mui-focused fieldset": { borderColor: isDark ? "rgba(255,255,255,0.28)" : p.primary },
      "& input": { color: p.text, WebkitTextFillColor: p.text },
      "&.Mui-error fieldset, &.Mui-error:hover fieldset": { borderColor: p.danger },
    },
    "& .MuiInputLabel-root.Mui-error": { color: p.danger },
    "& .MuiInputLabel-root": {
      color: isDark ? "rgba(255,255,255,0.62)" : "rgba(17,24,39,0.58)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: isDark ? "rgba(255,255,255,0.72)" : p.primaryHover,
    },
  };
};

const pillButtonBase = {
  borderRadius: uiRadii.pill,
  px: 2.8,
  minHeight: 40,
  textTransform: "none",
  boxShadow: "none",
};

export const primaryButtonSx = {
  ...pillButtonBase,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  fontWeight: 500,
  "&:hover": { bgcolor: "primary.dark", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "primary.disabled", color: "rgba(255,255,255,0.9)", boxShadow: "none" },
};

export const secondaryButtonSx = {
  ...pillButtonBase,
  color: "text.primary",
};

export const outlinedButtonSx = {
  ...pillButtonBase,
  color: "text.primary",
  borderColor: "ui.borderStrong",
  "&:hover": { borderColor: "text.secondary", bgcolor: "action.hover" },
};

export const destructiveButtonSx = {
  ...pillButtonBase,
  whiteSpace: "nowrap",
  bgcolor: "error.main",
  color: "#fff",
  fontWeight: 500,
  "&:hover": { bgcolor: "error.dark", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "error.disabled", color: "rgba(255,255,255,0.9)", boxShadow: "none" },
};

// Round outlined action button (edit, archive, sort...).
export const iconButtonSx = {
  flexShrink: 0,
  width: 42,
  height: 42,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: uiRadii.pill,
  color: "text.primary",
  "&:hover": { bgcolor: "action.hover", borderColor: "ui.borderStrong", transform: "translateY(-1px)" },
};

export const dangerIconButtonSx = {
  ...iconButtonSx,
  color: "error.main",
  borderColor: "ui.dangerBorder",
  "&:hover": { bgcolor: "ui.dangerSoft", borderColor: "error.main", transform: "translateY(-1px)" },
};

// Smaller on/off filter toggle in the toolbar.
export const toggleIconButtonSx = (active, color = "success.main") => ({
  width: 38,
  height: 38,
  border: "1px solid",
  borderColor: active ? color : "divider",
  color: active ? color : "text.secondary",
  "& svg": { fontSize: 19 },
  "&:hover": { bgcolor: "action.hover", borderColor: active ? color : "ui.borderStrong" },
});

export const sectionTitleSx = { fontWeight: 600, mb: 0.5 };
export const sectionLeadSx = { color: "text.secondary", mb: 2 };
